// Right-side slide-over drawer showing a single lead's full detail + unified
// activity/notes timeline + assignment history + (admins) status events.
//
// Rendered INSIDE LeadsPage (driven by `selectedLeadId`) rather than as a route
// so the inbox table state (filter tab / scroll / Mine-only) is preserved. The
// timeline feed comes from leadAction.getLeadTimeline (server-scoped); notes and
// "Log contact" reuse the same client_activities write path as the inbox.
import React, { useState } from 'react';
import {
  Activity,
  CalendarClock,
  Circle,
  FileText,
  Mail,
  Phone,
  RefreshCw,
  StickyNote,
  UserCog,
  X,
} from 'lucide-react';
import type { LeadRecord } from '../../hooks/useLeads';
import {
  useLeadTimeline,
  type TimelineActivity,
  type TimelineAssignmentEntry,
  type TimelineStatusEvent,
} from '../../hooks/useLeadTimeline';
import { ACTIVITY_META } from '../activities/activityMeta';
import type { ActivityType } from '../../hooks/useActivities';

// ── Local formatting helpers (kept self-contained so the drawer has no
//    coupling to LeadsPage internals). ──────────────────────────────────
function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  try {
    const then = new Date(iso).getTime();
    const diffMs = Math.max(0, Date.now() - then);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return new Date(iso).toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const SOURCE_BRAND: Record<string, { label: string; color: string }> = {
  facebook_lead_ads: { label: 'Facebook Lead Ad', color: '#1877f2' },
  meta_ads: { label: 'Meta Ads', color: '#1877f2' },
  google_ads: { label: 'Google Ads', color: '#4285f4' },
  website_form: { label: 'Website Form', color: '#21759b' },
  referral: { label: 'Referral', color: '#8b5cf6' },
  manual: { label: 'Manual', color: '#6b7280' },
  csv_import: { label: 'CSV Import', color: '#6b7280' },
  other: { label: 'Other', color: '#6b7280' },
};
function sourceBrand(source: string): { label: string; color: string } {
  return SOURCE_BRAND[source] ?? { label: source, color: '#6b7280' };
}

const ASSIGNMENT_REASON_LABEL: Record<string, string> = {
  initial_round_robin: 'Auto-assigned (round robin)',
  initial_least_loaded: 'Auto-assigned (least loaded)',
  initial_random: 'Auto-assigned (random)',
  initial_first_available: 'Auto-assigned (first available)',
  sla_escalation: 'Escalated (SLA breach)',
  manual_admin: 'Manually reassigned by admin',
  broker_decline: 'Reassigned (broker declined)',
  queued_to_business_hours: 'Queued to business hours',
  no_rotation_match: 'No rotation match',
  pool_exhausted: 'Unassigned (pool exhausted)',
  pool_exhausted_retry: 'Waiting for an available broker (will retry)',
  fallback_owner: 'Assigned to the fallback owner (no rotation could take it)',
};

// ── Small presentational pieces ─────────────────────────────────────────
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-gray-500">
      {label}
    </div>
    <div className="text-sm text-gray-100 mt-0.5 break-words">{children}</div>
  </div>
);

const ActivityRow: React.FC<{ a: TimelineActivity }> = ({ a }) => {
  const meta = ACTIVITY_META[(a.activityType as ActivityType)] ?? ACTIVITY_META.other;
  const Icon = meta.icon;
  return (
    <li className="relative pl-8 pb-4">
      <span className="absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 border border-gray-700">
        <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
      </span>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-gray-100">{meta.label}</span>
        <span
          className="text-[11px] text-gray-500 whitespace-nowrap"
          title={formatDateTime(a.occurredAt)}
        >
          {formatRelative(a.occurredAt)}
        </span>
      </div>
      {a.outcome && (
        <div className="text-xs text-gray-400 mt-0.5">Outcome: {a.outcome}</div>
      )}
      {a.notes && (
        <div className="text-xs text-gray-300 mt-1 whitespace-pre-wrap">
          {a.notes}
        </div>
      )}
      <div className="text-[11px] text-gray-500 mt-1">
        {a.brokerName || a.brokerId || 'Unknown'}
        {typeof a.durationSeconds === 'number' && a.durationSeconds > 0 && (
          <span> · {Math.round(a.durationSeconds / 60)} min</span>
        )}
      </div>
    </li>
  );
};

const AssignmentRow: React.FC<{ e: TimelineAssignmentEntry }> = ({ e }) => (
  <li className="relative pl-8 pb-3">
    <span className="absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 border border-gray-700">
      <UserCog className="h-3.5 w-3.5 text-indigo-400" />
    </span>
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-sm text-gray-100">
        {(e.reason && ASSIGNMENT_REASON_LABEL[e.reason]) || e.reason || 'Assigned'}
      </span>
      <span
        className="text-[11px] text-gray-500 whitespace-nowrap"
        title={formatDateTime(e.assignedAt)}
      >
        {formatRelative(e.assignedAt)}
      </span>
    </div>
    <div className="text-[11px] text-gray-500 mt-0.5">
      {e.brokerId ? `→ ${e.brokerId}` : 'Unassigned'}
    </div>
  </li>
);

const StatusEventRow: React.FC<{ s: TimelineStatusEvent }> = ({ s }) => (
  <li className="relative pl-8 pb-3">
    <span className="absolute left-0 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 border border-gray-700">
      <Activity className="h-3.5 w-3.5 text-emerald-400" />
    </span>
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-sm text-gray-100">
        {s.action === 'lead_updated' && s.newStatus
          ? `Status → ${s.newStatus}`
          : (s.action ?? 'Event')}
      </span>
      <span
        className="text-[11px] text-gray-500 whitespace-nowrap"
        title={formatDateTime(s.timestamp)}
      >
        {formatRelative(s.timestamp)}
      </span>
    </div>
    {(s.actorEmail || s.actorUid) && (
      <div className="text-[11px] text-gray-500 mt-0.5">
        by {s.actorEmail || s.actorUid}
      </div>
    )}
  </li>
);

export const LeadDetailDrawer: React.FC<{
  open: boolean;
  lead: LeadRecord | null;
  companyId: string | undefined;
  // Whether the caller may act (log contact / add note) on this lead. The
  // server is the real authority; this just hides controls the user can't use.
  canAct: boolean;
  onClose: () => void;
  onLogContact: (lead: LeadRecord) => void;
  // Adds a note via logActivity({ activityType:'note', linkedLeadId }). Returns
  // once the write resolves so the drawer can refresh the timeline.
  onAddNote: (lead: LeadRecord, note: string) => Promise<void>;
  // PR-8 mounts a "Link to loan" control here. Optional so PR-7 stands alone.
  loanLinker?: React.ReactNode;
  // Bumped by the parent after a page-level Log-contact submit so the drawer's
  // timeline re-fetches the newly written activity.
  refreshSignal?: number;
}> = ({
  open,
  lead,
  companyId,
  canAct,
  onClose,
  onLogContact,
  onAddNote,
  loanLinker,
  refreshSignal,
}) => {
  const { timeline, loading, error, refresh } = useLeadTimeline(
    companyId,
    open && lead ? lead.leadId : null,
    refreshSignal,
  );

  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  if (!open || !lead) return null;

  const fullName =
    [lead.firstName, lead.lastName].filter(Boolean).join(' ') ||
    lead.email ||
    '—';
  const brand = sourceBrand(lead.source);
  const campaign =
    lead.attribution?.utmCampaign ||
    (lead.source === 'referral' ? 'Partner Referral' : '(organic)');

  const handleAddNote = async () => {
    const body = note.trim();
    if (!body) return;
    setSavingNote(true);
    setNoteError(null);
    try {
      await onAddNote(lead, body);
      setNote('');
      refresh();
    } catch (err) {
      setNoteError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingNote(false);
    }
  };

  const hasTimeline =
    timeline &&
    (timeline.activities.length > 0 ||
      timeline.assignmentHistory.length > 0 ||
      timeline.statusEvents.length > 0 ||
      timeline.legacyNote);

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="flex-1 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel */}
      <aside className="w-full max-w-md h-full overflow-y-auto bg-gray-900 border-l border-gray-700 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 bg-gray-900/95 backdrop-blur px-5 py-4 border-b border-gray-800">
          <div>
            <h3 className="text-lg font-bold text-white">{fullName}</h3>
            <span
              className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
              style={{
                background: `${brand.color}20`,
                color: brand.color,
                border: `1px solid ${brand.color}50`,
              }}
            >
              <Circle
                className="w-1.5 h-1.5 flex-shrink-0"
                style={{ fill: brand.color, color: brand.color }}
              />
              {brand.label}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Lead summary */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Email">
              {lead.email ? (
                <a
                  href={`mailto:${lead.email}`}
                  className="inline-flex items-center gap-1 text-blue-300 hover:underline"
                >
                  <Mail className="w-3 h-3" />
                  {lead.email}
                </a>
              ) : (
                '—'
              )}
            </Field>
            <Field label="Phone">
              {lead.phone ? (
                <a
                  href={`tel:${lead.phone}`}
                  className="inline-flex items-center gap-1 text-blue-300 hover:underline"
                >
                  <Phone className="w-3 h-3" />
                  {lead.phone}
                </a>
              ) : (
                '—'
              )}
            </Field>
            <Field label="Campaign">{campaign}</Field>
            <Field label="Status">
              <span className="capitalize">{lead.status}</span>
            </Field>
            <Field label="Assigned to">
              {lead.assignedBrokerName || (
                <span className="text-amber-400">Unassigned</span>
              )}
            </Field>
            <Field label="Captured">
              <span title={formatDateTime(lead.capturedAt)}>
                {formatRelative(lead.capturedAt)}
              </span>
            </Field>
            <Field label="First contacted">
              {lead.firstContactedAt
                ? formatDateTime(lead.firstContactedAt)
                : '—'}
            </Field>
            <Field label="SLA due">
              {lead.firstContactDueAt ? (
                <span className="inline-flex items-center gap-1">
                  <CalendarClock className="w-3 h-3 text-amber-300" />
                  {formatDateTime(lead.firstContactDueAt)}
                </span>
              ) : (
                '—'
              )}
            </Field>
          </div>

          {/* PR-8 loan linker slot */}
          {loanLinker && (
            <div className="rounded-lg border border-gray-800 bg-gray-900/40 p-3">
              {loanLinker}
            </div>
          )}

          {/* Actions */}
          {canAct && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onLogContact(lead)}
                className="px-3 py-1.5 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs inline-flex items-center gap-1.5"
              >
                <Phone className="w-3.5 h-3.5" />
                Log contact
              </button>
            </div>
          )}

          {/* Add note */}
          {canAct && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                Add note
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 5000))}
                rows={2}
                maxLength={5000}
                placeholder="Add a note — appears in the timeline, attributed to you."
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500"
              />
              {noteError && (
                <p className="text-xs text-red-400 mt-1">{noteError}</p>
              )}
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleAddNote}
                  disabled={savingNote || !note.trim()}
                  className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingNote && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  <StickyNote className="w-3.5 h-3.5" />
                  Save note
                </button>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-white">Timeline</h4>
              <button
                onClick={refresh}
                className="text-gray-400 hover:text-white"
                title="Refresh timeline"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}
                />
              </button>
            </div>

            {error && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
                {error}
              </div>
            )}

            {loading && !timeline && (
              <div className="flex items-center text-gray-400 text-sm py-4">
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                Loading…
              </div>
            )}

            {!loading && !error && timeline && !hasTimeline && (
              <p className="text-sm text-gray-500 py-2">
                No activity yet. Log a contact or add a note to start the
                timeline.
              </p>
            )}

            {timeline && hasTimeline && (
              <ol className="mt-1">
                {timeline.activities.map((a) => (
                  <ActivityRow key={a.activityId} a={a} />
                ))}

                {timeline.assignmentHistory.length > 0 && (
                  <>
                    <li className="text-[10px] uppercase tracking-wider text-gray-600 pl-8 pb-1 pt-2">
                      Assignment history
                    </li>
                    {timeline.assignmentHistory.map((e, i) => (
                      <AssignmentRow key={`assign-${i}`} e={e} />
                    ))}
                  </>
                )}

                {timeline.statusEvents.length > 0 && (
                  <>
                    <li className="text-[10px] uppercase tracking-wider text-gray-600 pl-8 pb-1 pt-2">
                      Status events (admin)
                    </li>
                    {timeline.statusEvents.map((s, i) => (
                      <StatusEventRow key={`status-${i}`} s={s} />
                    ))}
                  </>
                )}

                {timeline.legacyNote && (
                  <li className="relative pl-8 pt-2">
                    <span className="absolute left-0 top-2.5 flex h-6 w-6 items-center justify-center rounded-full bg-gray-800 border border-gray-700">
                      <FileText className="h-3.5 w-3.5 text-gray-400" />
                    </span>
                    <div className="text-[10px] uppercase tracking-wider text-gray-600">
                      Captured note (legacy)
                    </div>
                    <div className="text-xs text-gray-300 mt-1 whitespace-pre-wrap">
                      {timeline.legacyNote}
                    </div>
                  </li>
                )}
              </ol>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default LeadDetailDrawer;
